import type { PackageOption } from "./types";

export interface GridCellEntry {
  id: number;
  date: string; // YYYY-MM-DD
  startTime: string | null;
  endTime: string | null;
  minutes: number;
  hours: number;
  note: string | null;
  syncStatus: "QUEUED" | "SENDING" | "SYNCED" | "FAILED" | "NEEDS_ATTENTION";
  isAvailability: boolean;
  isOverBudget?: boolean;
  isNonMember?: boolean;
  source: "HUB" | "BEXIO";
  billable: boolean;
  activityId?: number | null;
  activityName?: string | null;
  bexioMessage?: string | null;
  bexioId?: number | null;
}

export interface GridCellData {
  packageId: number;
  dateStr: string;
  totalMinutes: number;
  totalHours: number;
  entries: GridCellEntry[];
}

export interface GridPackageRow {
  packageId: number;
  packageName: string;
  clientName: string;
  clientLogoUrl: string | null;
  budgetHours: number | null;
  remainingHours: number | null;
  myUsedHours: number | null;
  isOverBudget: boolean;
  isMine: boolean;
  cells: Record<string, GridCellData>;
  rowTotalMinutes: number;
  rowTotalHours: number;
}

export interface MissedDay {
  dateStr: string;
  dayLabel: string;
  dayNum: number;
  monthLabel: string;
}

export interface WeekGridDayInfo {
  dateStr: string;
  dayLabel: string;
  dayNum: number;
  monthLabel: string;
  isToday: boolean;
  isPastWeekday: boolean;
  totalMinutes: number;
  totalHours: number;
}

export interface WeekGridData {
  days: WeekGridDayInfo[];
  rows: GridPackageRow[];
  columnTotals: Record<string, { totalMinutes: number; totalHours: number }>;
  grandTotalMinutes: number;
  grandTotalHours: number;
  missedDays: MissedDay[];
}

export interface RawGridEntryInput {
  id: number;
  date: string;
  startTime: string | null;
  endTime: string | null;
  minutes: number;
  note: string | null;
  syncStatus: "QUEUED" | "SENDING" | "SYNCED" | "FAILED" | "NEEDS_ATTENTION";
  isAvailability: boolean;
  isOverBudget?: boolean;
  isNonMember?: boolean;
  source: "HUB" | "BEXIO";
  billable: boolean;
  workPackageId: number;
  activity?: { id: number; name: string } | null;
  bexioMessage?: string | null;
  bexioId?: number | null;
}

/**
 * Format minutes to hours rounded to 1 decimal place.
 */
export function formatMinutesToHours(minutes: number): number {
  return Math.round((minutes / 60) * 10) / 10;
}

/**
 * Parse a human-friendly duration input (e.g. "1.5", "1.5h", "90", "90m", "1h 30m")
 * into positive minutes in 15-minute steps.
 */
export function parseHoursInputToMinutes(input: string | number): number | null {
  const mins = parseDuration(input);
  // More than a day is a typo (75h for 7.5h), never real work.
  return mins !== null && mins <= 24 * 60 ? mins : null;
}

function parseDuration(input: string | number): number | null {
  if (typeof input === "number") {
    if (isNaN(input) || input <= 0) return null;
    // Assume number <= 24 is hours, > 24 is minutes
    const mins = input <= 24 ? Math.round((input * 60) / 15) * 15 : Math.round(input / 15) * 15;
    return mins > 0 ? mins : null;
  }

  // "7,5" is how half the team writes 7.5.
  const raw = input.trim().toLowerCase().replace(",", ".");
  if (!raw) return null;

  // "1h 30m" or "1h30" format
  const complexMatch = raw.match(/^(\d+(?:\.\d+)?)\s*h(?:ours?)?(?:\s*(\d+)\s*(?:m(?:in)?)?)?$/);
  if (complexMatch) {
    const hours = parseFloat(complexMatch[1]);
    const mins = complexMatch[2] ? parseInt(complexMatch[2], 10) : 0;
    const total = Math.round((hours * 60 + mins) / 15) * 15;
    return total > 0 ? total : null;
  }

  // "45m" or "45min" format
  const minsMatch = raw.match(/^(\d+)\s*m(?:in)?$/);
  if (minsMatch) {
    const total = Math.round(parseInt(minsMatch[1], 10) / 15) * 15;
    return total > 0 ? total : null;
  }

  // Plain number (e.g. "1.5" or "2" -> hours; "90" -> 90 minutes)
  const plainNum = parseFloat(raw);
  if (isNaN(plainNum) || plainNum <= 0) return null;

  // Heuristic: values <= 24 are treated as hours (e.g. 1.5 -> 90 min)
  // values > 24 are treated as minutes (e.g. 45 -> 45 min)
  if (plainNum <= 24) {
    const total = Math.round((plainNum * 60) / 15) * 15;
    return total > 0 ? total : null;
  } else {
    const total = Math.round(plainNum / 15) * 15;
    return total > 0 ? total : null;
  }
}

/**
 * Identifies missed days (weekdays Mon-Fri before today with 0 logged minutes; today is still running)
 * per SPEC §4.1 & D10.
 */
export function identifyMissedDays(
  days: Array<{ dateStr: string; dayLabel: string; dayNum: number; monthLabel: string }>,
  columnTotals: Record<string, { totalMinutes: number; totalHours: number }>,
  todayStr: string
): MissedDay[] {
  const missed: MissedDay[] = [];
  // Days of week: index 0 to 4 are Mon, Tue, Wed, Thu, Fri
  for (let i = 0; i < Math.min(5, days.length); i++) {
    const day = days[i];
    if (day.dateStr < todayStr) {
      const minutes = columnTotals[day.dateStr]?.totalMinutes ?? 0;
      if (minutes === 0) {
        missed.push({
          dateStr: day.dateStr,
          dayLabel: day.dayLabel,
          dayNum: day.dayNum,
          monthLabel: day.monthLabel,
        });
      }
    }
  }
  return missed;
}

/**
 * Builds the full week grid data matrix (packages × days) with totals and missed days.
 */
export function buildWeekGridData({
  days,
  entries,
  packageOptions,
  pinnedPackageIds = [],
  todayStr,
}: {
  days: Array<{
    dateStr: string;
    dayLabel: string;
    dayNum: number;
    monthLabel: string;
    isToday: boolean;
  }>;
  entries: RawGridEntryInput[];
  packageOptions: PackageOption[];
  pinnedPackageIds?: number[];
  todayStr: string;
}): WeekGridData {
  // 1. Determine which packages to show as rows
  // All packages with entries this week + pinned packages + user's active/mine packages
  const rowPackageIds = new Set<number>();

  for (const entry of entries) {
    rowPackageIds.add(entry.workPackageId);
  }

  for (const id of pinnedPackageIds) {
    rowPackageIds.add(id);
  }

  // The budgets I am part of are always rows (owner, 2026-11-04), so nobody has to add them by hand.
  for (const pkg of packageOptions.filter((p) => p.isMine)) {
    rowPackageIds.add(pkg.id);
  }

  // If still none, add up to 3 available packages
  if (rowPackageIds.size === 0) {
    for (const pkg of packageOptions.slice(0, 3)) {
      rowPackageIds.add(pkg.id);
    }
  }

  // 2. Order rows: mine first, then alphabetical by client + package
  const rowsList: PackageOption[] = [];
  for (const id of rowPackageIds) {
    const opt = packageOptions.find((p) => p.id === id);
    if (opt) {
      rowsList.push(opt);
    }
  }

  rowsList.sort((a, b) => {
    if (a.isMine && !b.isMine) return -1;
    if (!a.isMine && b.isMine) return 1;
    return a.clientName.localeCompare(b.clientName) || a.name.localeCompare(b.name);
  });

  // 3. Index entries by `${workPackageId}:${date}`
  const entriesByCell = new Map<string, GridCellEntry[]>();
  const columnMinutes: Record<string, number> = {};
  for (const d of days) {
    columnMinutes[d.dateStr] = 0;
  }

  let grandTotalMinutes = 0;

  for (const raw of entries) {
    const key = `${raw.workPackageId}:${raw.date}`;
    if (!entriesByCell.has(key)) {
      entriesByCell.set(key, []);
    }

    const cellEntry: GridCellEntry = {
      id: raw.id,
      date: raw.date,
      startTime: raw.startTime,
      endTime: raw.endTime,
      minutes: raw.minutes,
      hours: formatMinutesToHours(raw.minutes),
      note: raw.note,
      syncStatus: raw.syncStatus,
      isAvailability: raw.isAvailability,
      isOverBudget: raw.isOverBudget ?? false,
      isNonMember: raw.isNonMember ?? false,
      source: raw.source,
      billable: raw.billable,
      activityId: raw.activity?.id,
      activityName: raw.activity?.name,
      bexioMessage: raw.bexioMessage,
      bexioId: raw.bexioId,
    };

    entriesByCell.get(key)!.push(cellEntry);

    // Accumulate column totals if date is in this week
    if (columnMinutes[raw.date] !== undefined) {
      columnMinutes[raw.date] += raw.minutes;
      grandTotalMinutes += raw.minutes;
    }
  }

  // 4. Construct package rows
  const rows: GridPackageRow[] = rowsList.map((pkg) => {
    const cells: Record<string, GridCellData> = {};
    let rowTotalMinutes = 0;

    for (const d of days) {
      const key = `${pkg.id}:${d.dateStr}`;
      const cellEntries = entriesByCell.get(key) ?? [];
      const totalMins = cellEntries.reduce((sum, e) => sum + e.minutes, 0);

      cells[d.dateStr] = {
        packageId: pkg.id,
        dateStr: d.dateStr,
        totalMinutes: totalMins,
        totalHours: formatMinutesToHours(totalMins),
        entries: cellEntries,
      };

      rowTotalMinutes += totalMins;
    }

    return {
      packageId: pkg.id,
      packageName: pkg.name,
      clientName: pkg.clientName,
      clientLogoUrl: pkg.clientLogoUrl,
      budgetHours: pkg.budgetHours,
      remainingHours: pkg.remainingHours ?? null,
      myUsedHours: pkg.myUsedHours ?? null,
      isOverBudget: pkg.isOverBudget ?? false,
      isMine: pkg.isMine,
      cells,
      rowTotalMinutes,
      rowTotalHours: formatMinutesToHours(rowTotalMinutes),
    };
  });

  // 5. Compute column totals & day metadata
  const columnTotals: Record<string, { totalMinutes: number; totalHours: number }> = {};
  const weekDaysInfo: WeekGridDayInfo[] = days.map((d, index) => {
    const mins = columnMinutes[d.dateStr] ?? 0;
    const isPastWeekday = index < 5 && d.dateStr < todayStr;

    columnTotals[d.dateStr] = {
      totalMinutes: mins,
      totalHours: formatMinutesToHours(mins),
    };

    return {
      dateStr: d.dateStr,
      dayLabel: d.dayLabel,
      dayNum: d.dayNum,
      monthLabel: d.monthLabel,
      isToday: d.isToday,
      isPastWeekday,
      totalMinutes: mins,
      totalHours: formatMinutesToHours(mins),
    };
  });

  // 6. Identify missed days
  const missedDays = identifyMissedDays(days, columnTotals, todayStr);

  return {
    days: weekDaysInfo,
    rows,
    columnTotals,
    grandTotalMinutes,
    grandTotalHours: formatMinutesToHours(grandTotalMinutes),
    missedDays,
  };
}

/**
 * Calculates shifted target dates when copying entries from previous week to target week (+7 days).
 */
export function calculateShiftedEntryDates<T extends { date: string | Date }>(
  entries: T[],
  sourceMondayStr: string,
  targetMondayStr: string
): Array<T & { targetDateStr: string }> {
  const srcMon = new Date(sourceMondayStr + "T00:00:00.000Z");
  const tgtMon = new Date(targetMondayStr + "T00:00:00.000Z");
  const diffDays = Math.round((tgtMon.getTime() - srcMon.getTime()) / (1000 * 60 * 60 * 24));

  return entries.map((e) => {
    const rawDateStr = e.date instanceof Date ? e.date.toISOString().slice(0, 10) : String(e.date).slice(0, 10);
    const d = new Date(rawDateStr + "T00:00:00.000Z");
    d.setUTCDate(d.getUTCDate() + diffDays);
    const targetDateStr = d.toISOString().slice(0, 10);
    return {
      ...e,
      targetDateStr,
    };
  });
}

/** Where this week's minutes stand with Bexio, for the "is my week done?" line above the grid. */
export function weekSyncStatus(grid: WeekGridData): { total: number; inBexio: number; waiting: number; problem: number } {
  const s = { total: 0, inBexio: 0, waiting: 0, problem: 0 };
  for (const row of grid.rows) {
    for (const cell of Object.values(row.cells)) {
      for (const e of cell.entries) {
        s.total += e.minutes;
        if (e.syncStatus === "SYNCED") s.inBexio += e.minutes;
        else if (e.syncStatus === "QUEUED" || e.syncStatus === "SENDING") s.waiting += e.minutes;
        else s.problem += e.minutes;
      }
    }
  }
  return s;
}
